// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 16-api-conventions.md
// @greenlight-spec: 39-gcp-installation.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AgentKindSchema, BILLING_PROVIDERS, BillingSessionSchema, BootstrapPluginMarketplaceBodySchema, GITHUB_ORG_LOGIN_REGEX, GreenlightErrorResponseSchema, GithubAppManifestHandshakeSchema, INSTALL_STEPS, InstallContactSchema, InstallStepStateSchema, InstallationStateSchema, IdpVerificationChecksSchema, InstallPaymentStatusSchema, ORG_SLUG_REGEX, PluginMarketplaceBootstrapResultSchema, } from '@greenlight/shared';
import { errorResponses } from './common.js';
const c = initContract();
const InstallStepParamsSchema = z.object({
    step: z.enum(INSTALL_STEPS),
});
/**
 * The per-cloud infra-summary block (docs/39), keyed on `GREENLIGHT_CLOUD_PROVIDER`.
 * `azureResourceGroup`/`azureSubscriptionId` below stay as flat fields for back-compat;
 * this is the canonical shape wizard/settings UI should render from.
 */
export const InstallCloudSchema = z.discriminatedUnion('provider', [
    z.object({
        provider: z.literal('azure'),
        resourceGroup: z.string().nullable(),
        subscriptionId: z.string().nullable(),
    }),
    z.object({
        provider: z.literal('gcp'),
        projectId: z.string().nullable(),
    }),
    z.object({
        provider: z.literal('aws'),
        accountId: z.string().nullable(),
        region: z.string().nullable(),
        eksClusterName: z.string().nullable(),
        ecrRegistryHost: z.string().nullable(),
        route53ZoneId: z.string().nullable(),
    }),
    z.object({
        provider: z.literal('localfake'),
    }),
]);
const InstallStatusOrgSchema = z.object({
    name: z.string(),
    slug: z.string(),
    appsSubdomain: z.string().nullable(),
    azureResourceGroup: z.string().nullable(),
    azureSubscriptionId: z.string().nullable(),
    cloud: InstallCloudSchema,
    supportedAgents: z.array(AgentKindSchema).min(1),
});
/**
 * The desktop installer app's prefill draft as the wizard reads it back
 * (docs/52 §From the installer to the wizard): one entry per drafted step that
 * is not yet complete. Never carries the identity-provider client secret — only
 * whether the server holds one for the drafted issuer and client.
 */
export const InstallDraftSchema = z.object({
    organization: z
        .object({ displayName: z.string(), supportedAgents: z.array(AgentKindSchema).min(1) })
        .optional(),
    github: z.object({ organization: z.string() }).optional(),
    contact: z.object({ name: z.string(), email: z.string() }).optional(),
    idp: z
        .object({
        provider: z.enum(['entra', 'okta', 'google', 'oidc']),
        issuerUrl: z.string(),
        clientId: z.string(),
        clientSecretHeld: z.boolean(),
    })
        .optional(),
});
export const InstallContextSchema = z.object({
    orgExists: z.boolean(),
    appsDomain: z.string(),
    greenlightBaseUrl: z.string().url(),
    controlPlaneHost: z.string(),
    azureResourceGroup: z.string().nullable(),
    azureSubscriptionId: z.string().nullable(),
    cloud: InstallCloudSchema,
    /** Who bills this install; only `direct` installs get the Payment method step. */
    billingProvider: z.enum(BILLING_PROVIDERS),
    /**
     * Drafts for steps not yet complete; empty with no draft, and always empty
     * once `installed_at` is set, since this route stays public.
     */
    draft: InstallDraftSchema,
});
/** The installer's work email, asked in the wizard's Organization step. */
export const InstallerEmailSchema = z.string().trim().toLowerCase().email().max(320);
const CreateOrgBodySchema = z.object({
    name: z.string().trim().min(2).max(255),
    slug: z
        .string()
        .trim()
        .regex(ORG_SLUG_REGEX, 'Slug must match org slug format (lowercase, hyphens).'),
    // Optional at creation: omitting it (existing installs, tests) keeps the
    // DB-level default of all first-class agents enabled.
    supportedAgents: z.array(AgentKindSchema).min(1).optional(),
    // Required by the wizard form; optional here so callers that predate the
    // field keep working. Stored as the org_setup step's `installer_email`.
    installerEmail: InstallerEmailSchema.optional(),
});
/**
 * The org's SCM connection, written only by the `installation` webhook — `null`
 * until GitHub reports the App installed, so the wizard can tell "App created"
 * from "App installed" instead of assuming the second follows the first.
 */
export const InstallScmInstallationSchema = z.object({
    installationId: z.string(),
    orgHandle: z.string(),
    suspended: z.boolean(),
});
export const InstallStatusSchema = z.object({
    org: InstallStatusOrgSchema,
    scmInstallation: InstallScmInstallationSchema.nullable(),
    installationState: InstallationStateSchema,
    installedAt: z.string().datetime().nullable(),
    /**
     * The recorded install contact, so a resumed wizard reads it back instead of
     * re-asking. `null` before it is named and, deliberately, on every read after
     * `installedAt` — this route stays public, and it must not keep serving a
     * named person's address once the wizard window has closed (docs/12
     * §Wizard-only window, the same rule the IdP step's admin email follows).
     */
    contact: InstallContactSchema.nullable(),
});
const AdvanceStepBodySchema = z.object({
    status: InstallStepStateSchema.shape.status,
    details: z.record(z.unknown()).optional(),
});
const BeginGithubAppBodySchema = z.object({
    /** GitHub org login to create the App in — required (docs/07 §GitHub App
     * creation flow); an empty value is rejected server-side (MANIFEST_INVALID). */
    githubOrg: z.string().optional(),
});
/** One NS record IT must create in the parent zone to delegate `appsDomain`. */
const NsRecordSchema = z.object({
    /** Record name — the delegated zone (`appsDomain`). */
    name: z.string(),
    type: z.literal('NS'),
    /** An authoritative nameserver assigned by the cloud's DNS service. */
    value: z.string(),
});
/** One Greenlight-managed platform A record written into the delegated zone. */
const PlatformARecordSchema = z.object({
    /** Record name — either the zone apex or wildcard host. */
    name: z.string(),
    type: z.literal('A'),
    /** Traefik LoadBalancer IP the record resolves to. */
    value: z.string(),
    /** Human-readable reason this record exists. */
    purpose: z.string(),
});
export const DnsSetupSchema = z.object({
    /** Control-plane host = the delegated zone apex (ADR-0016). */
    control_plane_host: z.string(),
    /** The single delegated zone (`GREENLIGHT_APPS_DOMAIN`). */
    apps_domain: z.string(),
    /** Illustrative app host (`<slug>.<apps_domain>`) shown to explain the wildcard. */
    example_app_host: z.string(),
    /** Traefik LoadBalancer IP the apex + wildcard A records point at. */
    ingress_target: z.string(),
    /** Cloud-DNS-assigned nameservers, read live from the zone via DnsProvider. */
    nameservers: z.array(z.string()),
    /** The NS records IT must create — the customer's only manual DNS action. */
    ns_records: z.array(NsRecordSchema),
    /** The platform A records Greenlight will upsert into the delegated zone. */
    platform_a_records: z.array(PlatformARecordSchema),
    /** Whether the platform A records were upserted by this response. */
    records_upserted: z.boolean(),
    /**
     * `private` publishes no public A record. Absent on a response from a
     * control plane that predates the field; callers treat that as public.
     */
    access_profile: z.enum(['public', 'private']).optional(),
});
const CheckStatusSchema = z.enum(['pass', 'fail', 'pending']);
/**
 * One verification check. `category` disambiguates a `fail`: `blocked` =
 * outbound DNS / ACME / cloud-API egress is blocked (locked-down VPC policy);
 * `mismatch` = the answer resolves but is wrong (delegation not done /
 * pointed elsewhere); `not_ready` = the resource exists but is not yet ready.
 */
const CheckResultSchema = z.object({
    status: CheckStatusSchema,
    detail: z.string(),
    category: z.enum(['blocked', 'mismatch', 'not_ready']).optional(),
});
export const ControlPlaneHttpsVerifySchema = z.object({
    /** True only when all four checks pass; the wizard advances on this. */
    ok: z.boolean(),
    checks: z.object({
        /** appsDomain NS records resolve and match the zone's cloud-DNS nameservers. */
        ns_precheck: CheckResultSchema,
        /** Apex A resolves to the ingress IP through a recursive resolver (delegation is live). */
        delegation: CheckResultSchema,
        /** cert-manager control-plane Certificate reports Ready=True (DNS-01 wildcard). */
        certificate: CheckResultSchema,
        /** HTTPS GET <greenlightBaseUrl>/healthz returns 200 with a valid Let's Encrypt chain. */
        https: CheckResultSchema,
    }),
});
export const IdpProviderSchema = z.enum(['entra', 'okta', 'google', 'oidc']);
/** OIDC issuers are always public HTTPS endpoints; the server appends the
 * fixed `/.well-known/openid-configuration` path, so this URL is the only
 * caller-controlled part of the probe target. */
export const IdpIssuerUrlSchema = z
    .string()
    .trim()
    .url()
    .max(2048)
    .refine((v) => v.startsWith('https://'), 'Issuer URL must use https.');
/**
 * The client fields are optional in the contract only, so a client that predates
 * the client-registration checks still gets the issuer-only result. Both write
 * surfaces always send all three and gate their Validate action on them being
 * filled; an absent field reports its client checks as inconclusive.
 */
const IdpClientIdSchema = z.string().trim().min(1).max(512);
/** Relayed only to the issuer's own token endpoint; never stored or echoed. */
const IdpClientSecretSchema = z.string().min(1).max(4096);
export const ValidateIdpBodySchema = z.object({
    issuer_url: IdpIssuerUrlSchema,
    provider: IdpProviderSchema.optional(),
    client_id: IdpClientIdSchema.optional(),
    client_secret: IdpClientSecretSchema.optional(),
});
/**
 * Candidate configuration for Settings → Identity's test-connection action: the
 * validate-body fields, all optional, since an empty body tests the stored
 * connection — whose client ID and Key Vault secret the server reads itself.
 */
export const TestIdentityBodySchema = z.object({
    issuer_url: IdpIssuerUrlSchema.optional(),
    provider: IdpProviderSchema.optional(),
    client_id: IdpClientIdSchema.optional(),
    client_secret: IdpClientSecretSchema.optional(),
});
/** Per-check result of verifying the issuer and the client registration. */
export const IdpValidationResultSchema = z.object({
    /** True when no check failed; the wizard enables save on this. */
    ok: z.boolean(),
    checks: IdpVerificationChecksSchema,
});
/** One role's group identifiers; bounded like the other free-text IdP fields. */
const RoleGroupArraySchema = z.array(z.string().trim().min(1).max(255)).max(32).optional();
/** One Workspace domain in the Google `hd` allowlist; compared case-insensitively at login. */
const HostedDomainSchema = z.string().trim().toLowerCase().min(1).max(255);
/**
 * Where platform roles come from (docs/10 §IdP group → platform role mapping):
 * `idp` recomputes roles from IdP groups at login, `greenlight` assigns them
 * in-product. Google is constrained to `greenlight` (no group claim) in the
 * body superRefine, so `idp` is unrepresentable for it.
 */
const RoleSourceBodySchema = z.enum(['idp', 'greenlight']);
/** Email address validator for the Greenlight-managed initial-admin bootstrap. */
const EmailSchema = z.string().trim().toLowerCase().email().max(320);
/** Validation shared by the install and Settings → Identity write bodies. */
function refineIdpBody(val, ctx) {
    if (val.provider === 'google' && hasRoleGroups(val.role_groups)) {
        // Google Workspace has no group claim and Greenlight no longer reads the
        // Admin Directory API, so there is no membership source to map. Google
        // orgs manage roles in the dashboard (docs/10 §IdP group → platform role
        // mapping); accepting a mapping here would silently never take effect.
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['role_groups'],
            message: 'Google Workspace roles are managed in the dashboard, not through IdP groups.',
        });
    }
    if (val.provider === 'google' && val.role_source === 'idp') {
        // Google delivers no groups, so an IdP-managed Google org would have no
        // org_admin after login. The schema rule makes `idp` unrepresentable for
        // Google (docs/10 §Google Workspace roles).
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['role_source'],
            message: 'Google Workspace is always Greenlight-managed; IdP-managed roles are not available.',
        });
    }
    if (val.role_source === 'idp' && (val.role_groups?.org_admin?.length ?? 0) === 0) {
        // An IdP-managed install with no org_admin group would leave the org with
        // no administrator after the first login (docs/10 §IdP-managed roles).
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['role_groups', 'org_admin'],
            message: 'IdP-managed roles require an org_admin group so the org has an administrator.',
        });
    }
}
export const ConfigureIdpBodySchema = z
    .object({
    provider: IdpProviderSchema,
    issuer_url: IdpIssuerUrlSchema,
    client_id: z.string().trim().min(1).max(512),
    /**
     * Written to Key Vault; never persisted in Postgres or echoed back. Omitted
     * only to use the client secret a prefill draft holds, which the server
     * accepts when provider, issuer, and client ID match that draft and
     * refuses with `install.idp_client_secret_required` otherwise.
     */
    client_secret: z.string().min(1).max(4096).optional(),
    /**
     * Generic OIDC only: the label rendered on the sign-in button ("Sign in
     * with <display_name>"); the provider has no brand to render. Defaults to
     * "SSO" when omitted.
     */
    display_name: z.string().trim().min(1).max(64).optional(),
    /**
     * Where platform roles come from (docs/10 §IdP group → platform role
     * mapping). Optional for back-compat; an absent value is inferred
     * server-side (`idp` iff a mapping is configured, else `greenlight`; Google
     * always `greenlight`). The wizard always sends it explicitly.
     */
    role_source: RoleSourceBodySchema.optional(),
    group_claim: z.string().trim().min(1).max(255).default('groups'),
    /**
     * IdP group identifiers granted each platform role at login (docs/10 §IdP
     * group → platform role mapping). Identifiers must match what the group
     * claim emits (Entra object IDs, Okta claim values, Google group emails).
     * The wizard writes the admin-group field as `org_admin`; a non-empty
     * `user` array additionally restricts who can sign in.
     */
    role_groups: z
        .object({
        org_admin: RoleGroupArraySchema,
        admin: RoleGroupArraySchema,
        user: RoleGroupArraySchema,
    })
        .optional(),
    /**
     * Google only: the Workspace domains login is restricted to (`hd` claim).
     * A Workspace may span several domains and Google reports the signing-in
     * account's own, so this is a list. Empty or omitted applies no
     * Greenlight-side domain check — sign-in is then bounded only by the Google
     * OAuth client's own user type (docs/10 §Google Workspace roles).
     */
    hosted_domains: z.array(HostedDomainSchema).max(64).optional(),
    /**
     * Greenlight-managed only: the email of the initial `org_admin`. On save
     * Greenlight pre-creates that `users` row (`idp_subject: null`), linked on
     * the first matching SSO login (docs/10 §Greenlight-managed roles). Required
     * when `role_source` resolves to `greenlight` (enforced server-side, where
     * the resolved source is known).
     */
    initial_admin_email: EmailSchema.optional(),
})
    .superRefine(refineIdpBody);
/** True when a `role_groups` body carries at least one group in any role. */
function hasRoleGroups(roleGroups) {
    if (!roleGroups)
        return false;
    return ((roleGroups.org_admin?.length ?? 0) > 0 ||
        (roleGroups.admin?.length ?? 0) > 0 ||
        (roleGroups.user?.length ?? 0) > 0);
}
/** The prefill protocol version the desktop app posts; a mismatch is refused by name. */
export const INSTALL_PREFILL_SCHEMA_VERSION = 1;
/**
 * `POST /api/install/prefill` (docs/52 §From the installer to the wizard). Each
 * field is validated with the schema its own wizard step's route uses, and
 * unknown keys are refused. The organization part may arrive over the bootstrap
 * address; the others are accepted only on a request that reached the control
 * plane over HTTPS for its own host.
 */
export const InstallPrefillBodySchema = z
    .object({
    schemaVersion: z.literal(INSTALL_PREFILL_SCHEMA_VERSION, {
        errorMap: () => ({
            message: `schemaVersion must be ${INSTALL_PREFILL_SCHEMA_VERSION}.`,
        }),
    }),
    organization: z
        .object({
        displayName: CreateOrgBodySchema.shape.name,
        supportedAgents: z.array(AgentKindSchema).min(1),
    })
        .strict()
        .optional(),
    github: z
        .object({
        organization: z
            .string()
            .trim()
            .regex(GITHUB_ORG_LOGIN_REGEX, 'Not a valid GitHub organization name.'),
    })
        .strict()
        .optional(),
    contact: z
        .object({
        name: InstallContactSchema.shape.contact_name,
        email: InstallContactSchema.shape.contact_email,
    })
        .strict()
        .optional(),
    idp: z
        .object({
        provider: IdpProviderSchema,
        issuerUrl: IdpIssuerUrlSchema,
        clientId: IdpClientIdSchema,
        clientSecret: IdpClientSecretSchema.optional(),
    })
        .strict()
        .optional(),
})
    .strict();
export const INSTALL_PREFILL_PARTS = ['organization', 'github', 'contact', 'idp'];
export const InstallPrefillResultSchema = z.object({
    /** The parts stored as drafts by this post. */
    accepted: z.array(z.enum(INSTALL_PREFILL_PARTS)),
    /** The parts this post named and the server did not store, each with why. */
    refused: z.array(z.object({
        part: z.enum(INSTALL_PREFILL_PARTS),
        code: z.enum(['install.step_complete', 'install.prefill_requires_https']),
        message: z.string(),
    })),
    /** True when the server now holds a client secret for the drafted issuer and client. */
    clientSecretHeld: z.boolean(),
});
/**
 * Settings → Identity reconfigure body (docs/28 §/settings). Same fields as
 * {@link ConfigureIdpBodySchema}, but `client_secret` is optional: an org admin
 * re-saving the same provider may leave it blank to keep the stored Key Vault
 * secret. A provider swap still requires a secret (enforced server-side, since
 * the new provider has no vault entry to fall back to).
 */
export const ReconfigureIdentityBodySchema = z
    .object({
    provider: IdpProviderSchema,
    issuer_url: IdpIssuerUrlSchema,
    client_id: z.string().trim().min(1).max(512),
    /** Written to Key Vault; never persisted in Postgres or echoed back. */
    client_secret: z.string().min(1).max(4096).optional(),
    /**
     * Generic OIDC only: the label rendered on the sign-in button ("Sign in
     * with <display_name>"); the provider has no brand to render. Defaults to
     * "SSO" when omitted.
     */
    display_name: z.string().trim().min(1).max(64).optional(),
    /**
     * Where platform roles come from (docs/10 §IdP group → platform role
     * mapping). Switching to `greenlight` freezes current roles; switching to
     * `idp` requires an `org_admin` group (refined below). Google is constrained
     * to `greenlight`. Optional for back-compat with older clients.
     */
    role_source: RoleSourceBodySchema.optional(),
    group_claim: z.string().trim().min(1).max(255).default('groups'),
    role_groups: z
        .object({
        org_admin: RoleGroupArraySchema,
        admin: RoleGroupArraySchema,
        user: RoleGroupArraySchema,
    })
        .optional(),
    /**
     * Google only: the Workspace domains login is restricted to (`hd` claim).
     * A Workspace may span several domains and Google reports the signing-in
     * account's own, so this is a list. Empty or omitted applies no
     * Greenlight-side domain check — sign-in is then bounded only by the Google
     * OAuth client's own user type (docs/10 §Google Workspace roles).
     */
    hosted_domains: z.array(HostedDomainSchema).max(64).optional(),
})
    .superRefine(refineIdpBody);
export const BeginGithubAppResultSchema = GithubAppManifestHandshakeSchema.partial().extend({
    /** Whether the App already exists for this org (re-install detection). */
    alreadyInstalled: z.boolean(),
    /** Created App handle, present when alreadyInstalled. */
    appSlug: z.string().optional(),
    /** GitHub installation URL, present when alreadyInstalled. */
    installUrl: z.string().url().optional(),
});
export const installContract = c.router({
    getContext: {
        method: 'GET',
        path: '/api/install/context',
        responses: {
            200: InstallContextSchema,
            ...errorResponses,
        },
        summary: 'Bootstrap install context (apps domain, cloud metadata, optional org slug). Always succeeds before an org row exists. Public — no auth required before install completes.',
    },
    createOrg: {
        method: 'POST',
        path: '/api/install/org',
        body: CreateOrgBodySchema,
        responses: {
            201: InstallStatusSchema,
            ...errorResponses,
        },
        summary: 'Create the single org row and seed default policies. Callable without auth only while no org exists. Public — no auth required before install completes.',
    },
    getStatus: {
        method: 'GET',
        path: '/api/install/status',
        responses: {
            200: InstallStatusSchema,
            ...errorResponses,
        },
        summary: 'Returns install status, org metadata, and per-step progress. Public — no auth required before install completes.',
    },
    advanceStep: {
        method: 'POST',
        path: '/api/install/steps/:step',
        pathParams: InstallStepParamsSchema,
        body: AdvanceStepBodySchema,
        responses: {
            200: z.object({ installationState: InstallationStateSchema }),
            ...errorResponses,
        },
        summary: 'Mark an install step with a new status. Idempotent: re-posting the same status is a no-op. Public — no auth required before install completes.',
    },
    beginGithubApp: {
        method: 'POST',
        path: '/api/install/github-app/manifest',
        body: BeginGithubAppBodySchema,
        responses: {
            200: BeginGithubAppResultSchema,
            ...errorResponses,
        },
        summary: 'Generate the GitHub App manifest + CSRF state for the install wizard to submit to GitHub. Gated on the verified-URL handoff. If the App already exists, returns the install link instead. Public — no auth required before install completes.',
    },
    resetGithubApp: {
        method: 'POST',
        path: '/api/install/github-app/reset',
        body: z.object({}).optional(),
        responses: {
            200: z.object({ installationState: InstallationStateSchema }),
            ...errorResponses,
        },
        summary: 'Discard the github_app step stored App identity (app_id/app_slug) and return the step to not-started, so the wizard create form reappears. Mid-wizard only — recreateGithubApp under Settings is the post-install equivalent. Public — no auth required before install completes.',
    },
    bootstrapPluginMarketplace: {
        method: 'POST',
        path: '/api/install/plugin-marketplace',
        body: BootstrapPluginMarketplaceBodySchema,
        responses: {
            200: PluginMarketplaceBootstrapResultSchema,
            ...errorResponses,
        },
        summary: 'Create a public or private customer-owned plugin marketplace repo (render the bundled template, create the repo, commit main, persist the row, audit) and mark the plugin_marketplace step complete. Gated on the GitHub App step being complete. Idempotent by repository name and visibility: recreates a deleted repo, no-ops an up-to-date one. Public endpoint — no auth required before install completes.',
    },
    saveContact: {
        method: 'POST',
        path: '/api/install/contact',
        body: InstallContactSchema,
        responses: {
            200: z.object({ installationState: InstallationStateSchema }),
            ...errorResponses,
        },
        summary: "Record the administrator Shift contacts about this install's trial and subscription, and mark the contact step complete. Required before the wizard can finish. Re-postable while the wizard is open, so a correction overwrites. Public — no auth required before install completes.",
    },
    getPaymentStatus: {
        method: 'GET',
        path: '/api/install/payment',
        responses: {
            200: InstallPaymentStatusSchema,
            ...errorResponses,
        },
        summary: "Whether the install's billing account has a payment method on file, for the wizard's Payment method step. The reporting key stays server-side and nothing else is returned. Direct installs only; rate-limited. Public — no auth required before install completes.",
    },
    requestPaymentSession: {
        method: 'POST',
        path: '/api/install/payment/session',
        body: z.object({}).strict(),
        responses: {
            200: BillingSessionSchema,
            ...errorResponses,
            503: GreenlightErrorResponseSchema,
        },
        summary: "Request a Stripe-hosted page that saves a payment method, returning to the wizard's Payment method step. Only a setup page is handed out; once a card is saved this refuses with 409 billing.payment_method_on_file and the card is managed from Settings → Billing after sign-in. Direct installs only; audited, rate-limited. Public — no auth required before install completes.",
    },
    prefill: {
        method: 'POST',
        path: '/api/install/prefill',
        body: InstallPrefillBodySchema,
        responses: {
            200: InstallPrefillResultSchema,
            ...errorResponses,
        },
        summary: "Store the desktop installer app's draft of the wizard's early answers against steps not yet complete, for the wizard to show for confirmation. Completes nothing. The organization part is accepted over any transport; the github, contact, and idp parts only on a request that reached the control plane over HTTPS for its own host. A client secret is held in the vault and never returned. Public — refused once the install is complete.",
    },
    validateIdp: {
        method: 'POST',
        path: '/api/install/idp/validate',
        body: ValidateIdpBodySchema,
        responses: {
            200: IdpValidationResultSchema,
            ...errorResponses,
        },
        summary: "Probe the issuer's /.well-known/openid-configuration and, while the install window is open, the client registration behind the supplied client ID and secret. Read-only; no state change. Public — the client-registration checks run only while installed_at is null, so a completed install answers with the issuer checks alone.",
    },
    configureIdp: {
        method: 'POST',
        path: '/api/install/idp',
        body: ConfigureIdpBodySchema,
        responses: {
            200: z.object({ installationState: InstallationStateSchema }),
            ...errorResponses,
        },
        summary: 'Validate the issuer discovery document, store the client secret in the vault, write the org IdP configuration, and mark the idp step complete. Replaces any existing provider and revokes active sessions. Gated on the verified-URL handoff. Public — no auth required before install completes.',
    },
    getDnsSetup: {
        method: 'GET',
        path: '/api/install/dns/setup',
        responses: {
            200: DnsSetupSchema,
            ...errorResponses,
        },
        summary: 'Read the zone nameservers + ingress IP and return the DNS records the install wizard will display. Does not mutate DNS. Public — no auth required before install completes.',
    },
    upsertPlatformDnsRecords: {
        method: 'POST',
        path: '/api/install/dns/platform-records',
        body: z.object({}),
        responses: {
            200: DnsSetupSchema,
            ...errorResponses,
        },
        summary: 'Upsert the apex + wildcard A records in the delegated zone (idempotent, not gated on delegation), and return the DNS setup. Public — no auth required before install completes.',
    },
    verifyControlPlaneHttps: {
        method: 'POST',
        path: '/api/install/verify/control-plane-https',
        body: z.object({}),
        responses: {
            200: ControlPlaneHttpsVerifySchema,
            ...errorResponses,
        },
        summary: 'Run the DNS-delegation, certificate-readiness, and HTTPS-healthz checks for the control-plane host. On all-pass marks the dns + control_plane_https steps complete. Public — no auth required before install completes.',
    },
});
//# sourceMappingURL=install.js.map